import type { ReactNode } from 'react';
import { Navigate, Route, Routes } from 'react-router';
import { Home } from '@/routes/Home';
import { LogPrayer } from '@/routes/LogPrayer';
import { NextPrayer } from '@/routes/NextPrayer';
import { Onboarding } from '@/routes/Onboarding';
import { Report } from '@/routes/Report';
import { Settings } from '@/routes/Settings';
import { Welcome } from '@/routes/Welcome';
import { useAppState } from '@/state/useAppState';

/** Nothing but the entry flow is reachable until there is someone to record for. */
function RequireProfile({ children }: { children: ReactNode }) {
  const { ready, profile } = useAppState();
  if (!ready) return null;
  if (!profile) return <Navigate to="/welcome" replace />;
  return <>{children}</>;
}

/**
 * Onboarding is reachable both with a profile and without one, so neither gate
 * above fits — but it still cannot render before the stored profile has been
 * read. Its fields seed themselves from that profile once, on mount, and a
 * cold load straight onto this route used to beat the read: the name came up
 * blank and the age at the default, over a profile that had both.
 */
function WhenLoaded({ children }: { children: ReactNode }) {
  const { ready } = useAppState();
  if (!ready) return null;
  return <>{children}</>;
}

function EntryOnly({ children }: { children: ReactNode }) {
  const { ready, profile } = useAppState();
  if (!ready) return null;
  if (profile) return <Navigate to="/" replace />;
  return <>{children}</>;
}

export function App() {
  return (
    <Routes>
      <Route
        path="/welcome"
        element={
          <EntryOnly>
            <Welcome />
          </EntryOnly>
        }
      />
      <Route
        path="/onboarding"
        element={
          <WhenLoaded>
            <Onboarding />
          </WhenLoaded>
        }
      />
      <Route
        path="/"
        element={
          <RequireProfile>
            <Home />
          </RequireProfile>
        }
      />
      <Route
        path="/log"
        element={
          <RequireProfile>
            <NextPrayer />
          </RequireProfile>
        }
      />
      <Route
        path="/log/:prayerId"
        element={
          <RequireProfile>
            <LogPrayer />
          </RequireProfile>
        }
      />
      <Route
        path="/settings"
        element={
          <RequireProfile>
            <Settings />
          </RequireProfile>
        }
      />
      <Route
        path="/report"
        element={
          <RequireProfile>
            <Report />
          </RequireProfile>
        }
      />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
